/// <amd-module name="Release/Landing/LeftPanel/ActivePipelines/ActivePipelinePanelItemText" />
import * as React from "react";

import { Tooltip } from "VSSUI/TooltipEx";
import { css } from "VSSUI/Util";

import { VssComponent, IVssComponentProperties, IVssComponentContext } from "VSS/Platform/Layout";
import { IVssPageContext } from "VSS/Platform/Context";
import { Status, IStatusProps, Statuses, StatusSize } from "VSSUI/Status";

import { Deployment, ReleaseApproval, DeploymentOperationStatus, DeploymentStatus } from "Release/Client/Contracts/Release";
import { getIconStatusProps } from "Release/EnvironmentNode/Helpers";
import { ComputedDeploymentStatus } from "Release/EnvironmentNode/Types";
import { getComputedStatus, getStatusInfo } from "Release/EnvironmentNode/StatusComputer";
import * as Resources from "Release/Landing/Resources";

import "VSS/LoaderPlugins/Css!Release/Landing/LeftPanel/ActivePipelinePanelItemText";

export interface IActivePipelinePanelItemTextProps extends IVssComponentProperties {
    pageContext: IVssPageContext;
    pendingApproval: ReleaseApproval | null | undefined;
    lastDeployment: Deployment | null | undefined;
    pendingApprovalDate: string;
}

export class ActivePipelinePanelItemText extends VssComponent<IActivePipelinePanelItemTextProps, {}>{
    public render(): React.ReactNode {
        if (this.props.pendingApproval) {
            return this._getPendingApprovalView(this.props.pendingApproval);
        }
        else if (this.props.lastDeployment) {
            return this._getLastDeploymentView(this.props.lastDeployment);
        }
        else {
            return (<span className="active-rd-subtext no-deployments"> {Resources.NoDeploymentText} </span>);
        }
    }

    private _getPendingApprovalView(approval: ReleaseApproval): React.ReactNode {
        const pendingApprovalEnvironmentName: string = approval && approval.releaseEnvironment ? approval.releaseEnvironment.name : "";

        return (
            <div className={"active-rd-subtext flex-row flex-center"}>
                <Status {...Statuses.Waiting} size={StatusSize.s} className="active-rd-left-pane-status-icon pending-icon" />
                <Tooltip text={pendingApprovalEnvironmentName} overflowOnly={true}>
                    <div className={"active-rd-approval-subtext pending-subtext"}>{pendingApprovalEnvironmentName}</div>
                </Tooltip>
            </div>
        );
    }

    private _getLastDeploymentView(deployment: Deployment): React.ReactNode {
        const isPendingManualIntervention: boolean = deployment && deployment.operationStatus ? deployment.operationStatus === DeploymentOperationStatus.ManualInterventionPending : false;

        const computedStatus: ComputedDeploymentStatus = getComputedStatus(deployment.deploymentStatus || DeploymentStatus.Undefined, deployment.operationStatus || DeploymentOperationStatus.Undefined);
        const statusIndicator = getStatusInfo(computedStatus).statusIndicator;

        const statusProps: IStatusProps = getIconStatusProps(statusIndicator);
        const environmentName: string = deployment && deployment.releaseEnvironment ? deployment.releaseEnvironment.name : "";
        if (isPendingManualIntervention) {
            return this._getPendingManualInterventionSubtext(environmentName);
        }
        else {
            return this._getLastDeploymentSubtext(environmentName, statusProps);
        }
    }

    private _getPendingManualInterventionSubtext(environmentName: string): React.ReactNode {
        return (
            <div className={"active-rd-subtext flex-row flex-center"}>
                <Status {...Statuses.Waiting} size={StatusSize.s} className="active-rd-left-pane-status-icon flex-noshrink" />
                <Tooltip text={environmentName} overflowOnly={true}>
                    <div className={"active-rd-intervention-subtext pending-subtext"}>{environmentName}</div>
                </Tooltip>
            </div>
        );
    }

    private _getLastDeploymentSubtext(environmentName: string, statusProps: IStatusProps): React.ReactNode {
        return (
            <div className={"active-rd-subtext flex-row flex-center"}>
                <Status {...statusProps} size={StatusSize.s} className={css("active-rd-left-pane-status-icon flex-noshrink")} />
                <Tooltip text={environmentName} overflowOnly={true}>
                    <div className={"active-rd-environment-name-tooltip active-rd-subtitle-overflow"}>{environmentName}</div>
                </Tooltip>
            </div>
        );
    }

    private _getDeploymentIcon(deployment: Deployment): string {
        if (!deployment) {
            return "";
        }

        let icon: string = "";
        switch (deployment.deploymentStatus) {
            case DeploymentStatus.NotDeployed:
                icon = "Clock";
                break;
            case DeploymentStatus.InProgress:
                icon = "ProgressLoopOuter";
                break;
            case DeploymentStatus.PartiallySucceeded:
                icon = "AlertSolid";
                break;
            case DeploymentStatus.Succeeded:
                icon = "SkypeCircleCheck";
                break;
            case DeploymentStatus.Failed:
                icon = "StatusErrorFull";
                break;
        }

        return icon;
    }
}
